const allowedTags = new Set(['B', 'BR', 'DIV', 'EM', 'I', 'LI', 'OL', 'P', 'SPAN', 'STRONG', 'U', 'UL']);
const allowedStyles = new Set(['background-color', 'color', 'text-align']);

export function containsHtml(value: string) {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

export function sanitizeRichText(html: string) {
  if (typeof window === 'undefined') {
    return stripHtml(html);
  }

  const template = document.createElement('template');
  template.innerHTML = html;

  sanitizeNode(template.content);

  return template.innerHTML.trim();
}

export function sanitizeEditorRichText(html: string) {
  if (typeof window === 'undefined') {
    return stripHtml(html);
  }

  const template = document.createElement('template');
  template.innerHTML = html;

  sanitizeNode(template.content);
  normalizeParagraphs(template.content);

  return template.innerHTML.trim();
}

export function toRichTextHtml(value: string) {
  return containsHtml(value)
    ? sanitizeRichText(value)
    : value
        .trim()
        .split(/\r\n|\r|\n/)
        .map(escapeHtml)
        .join('<br>');
}

export function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, '').trim();
}

function normalizeParagraphs(parent: ParentNode) {
  Array.from(parent.childNodes).forEach((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node as HTMLElement;

    if (element.tagName === 'P') {
      const replacement = document.createElement('div');

      Array.from(element.attributes).forEach((attribute) => {
        replacement.setAttribute(attribute.name, attribute.value);
      });

      replacement.replaceChildren(...Array.from(element.childNodes));
      element.replaceWith(replacement);
      normalizeParagraphs(replacement);
      return;
    }

    normalizeParagraphs(element);
  });
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function sanitizeNode(parent: ParentNode) {
  Array.from(parent.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      node.remove();
      return;
    }

    const element = node as HTMLElement;

    if (!allowedTags.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      sanitizeNode(parent);
      return;
    }

    Array.from(element.attributes).forEach((attribute) => {
      if (attribute.name.toLowerCase() !== 'style') {
        element.removeAttribute(attribute.name);
      }
    });

    sanitizeStyle(element);
    sanitizeNode(element);
  });
}

function sanitizeStyle(element: HTMLElement) {
  const allowedStyleValues = new Map<string, string>();

  allowedStyles.forEach((styleName) => {
    const value = element.style.getPropertyValue(styleName);

    if (isSafeStyleValue(value)) {
      allowedStyleValues.set(styleName, value);
    }
  });

  element.removeAttribute('style');
  allowedStyleValues.forEach((value, styleName) => {
    element.style.setProperty(styleName, value);
  });
}

function isSafeStyleValue(value: string) {
  return value.length > 0 && !/url|expression|javascript/i.test(value);
}
